import moment from 'moment';
import {
  REGION_CATEGORY_FILTER_OPTIONS,
  USER_PORTFOLIO_WEIGHT_FILTER_OPTIONS,
  USER_RISK_PROFILE_FILTER_OPTIONS,
  USER_SEGMENT_FILTER_OPTIONS,
  usersFilterEnumOptions,
} from 'utils/constants';
import {depositStatus, profileCompletionEnum} from 'utils/mock';

/**
 * Turns the filter state into something the page can show and undo.
 *
 * Filters used to be write-only: you opened the panel, chose values, hit Save,
 * and the only evidence anything had been applied was that the row count
 * changed. There was no way to see what was on, and no way to remove one
 * without reopening the panel and clearing the field by hand — or switching
 * tabs, which silently wiped the lot.
 *
 * Every key here matches a field the panel collects and `buildUserFilterInput`
 * forwards, so a chip removing a key removes exactly the thing the query is
 * filtering on.
 */

const labelFrom = (options, value) =>
  options.find(option => option.value === value)?.label ?? value;

const DATE_KEYS = [
  'startDate',
  'endDate',
  'walletTransactionStartDate',
  'walletTransactionEndDate',
];

/**
 * How each filter presents itself.
 *
 * `username` and `kycStatus` are deliberately absent: the first is the search
 * box, which shows its own value, and the second is the tab.
 */
const DESCRIPTORS = {
  regionCategory: {name: 'Region', options: REGION_CATEGORY_FILTER_OPTIONS},
  riskProfile: {name: 'Risk', options: USER_RISK_PROFILE_FILTER_OPTIONS},
  userSegments: {name: 'Segment', options: USER_SEGMENT_FILTER_OPTIONS},
  portfolioWeight: {
    name: 'Portfolio',
    options: USER_PORTFOLIO_WEIGHT_FILTER_OPTIONS,
  },
  nationality: {name: 'Nationality'},
  city: {name: 'City'},
  country: {name: 'Citizenship'},
  profileCompletion: {name: 'Profile', options: profileCompletionEnum},
  depositStatus: {name: 'Transaction status', options: depositStatus},
  walletTransactionType: {name: 'Transaction type'},
  hasMadeReferral: {name: 'Has referral', options: usersFilterEnumOptions},
  hasMadeDeposit: {name: 'Deposited', options: usersFilterEnumOptions},
  hasMadeInvestment: {name: 'Invested', options: usersFilterEnumOptions},
  startDate: {name: 'Joined after'},
  endDate: {name: 'Joined before'},
  walletTransactionStartDate: {name: 'Transacted after'},
  walletTransactionEndDate: {name: 'Transacted before'},
};

/** Whether a stored value counts as a filter the user actually set. */
const isSet = value => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  // "Either" is the panel's way of saying no preference.
  if (value === 'EITHER') return false;
  return true;
};

const displayValue = (key, value, descriptor) => {
  if (DATE_KEYS.includes(key)) {
    const parsed = moment.isMoment(value) ? value : moment(value);
    return parsed.isValid() ? parsed.format('D MMM YYYY') : String(value);
  }
  if (Array.isArray(value))
    return value
      .map(item => labelFrom(descriptor.options ?? [], item))
      .join(', ');
  if (descriptor.options) return labelFrom(descriptor.options, value);
  // Raw enums arriving without a descriptor still read as words.
  return typeof value === 'string' && value === value.toUpperCase()
    ? value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ')
    : String(value);
};

/** `[{key, name, display}]` for everything currently applied. */
export const activeFilterChips = (filterValues = {}) =>
  Object.keys(DESCRIPTORS)
    .filter(key => isSet(filterValues[key]))
    .map(key => ({
      key,
      name: DESCRIPTORS[key].name,
      display: displayValue(key, filterValues[key], DESCRIPTORS[key]),
    }));

/** How many filters are applied — what the Filter button reports. */
export const activeFilterCount = (filterValues = {}) =>
  activeFilterChips(filterValues).length;

/** Everything the panel owns, cleared. Search and tab are left alone. */
export const clearedFilterValues = (filterValues = {}) => {
  const next = {...filterValues};
  Object.keys(DESCRIPTORS).forEach(key => delete next[key]);
  return next;
};
